import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { Select } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import classnames from "classnames";
import styled from "styled-components";
import { stripSubgroupFromTitle } from "utils/column/getColumnLabelWithContext";

import { useLengthConversion } from "hooks/useUnitConversion";

import { IGroupBy, IGroupByListColumn } from "models";

import useFilteredGeoCalculationsJobs from "components/org-settings/hooks/useFilteredGeoCalculationsJobs";
import { useGetGeoCalculationsJobs } from "components/org-settings/hooks/useGetGeoCalculationsJobs";
import { GeoCalculationsJob } from "components/org-settings/models/GeoCalculations";

import { PresetDropdownContext } from "../filter/focus/PresetDropdown";
import FieldGroup from "./FieldGroup";
import { groupFields } from "./helpers";
import { useSelectedCategory } from "./hooks";
import useOrgGeoSelector from "./hooks/useOrgGeoSelector";

interface OrgGeoCategoryProps {
  toolbarActive?: boolean;
  toolbarFocusedIndex?: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelectRef?: (index: number, selectEl: any | null) => void;
  onFieldClick?: (fieldData: FieldSelectionData) => void;
  onFieldsAvailable?: (fieldsCount: number) => void;
  onFieldItemRef?: (index: number, el: HTMLElement | null) => void;
  active?: boolean;
  focusedIndex?: number | null;
}

interface FieldSelectionData {
  field: IGroupByListColumn;
  onChange?: (
    value: IGroupBy | IGroupByListColumn | Record<string, unknown>,
    isForecastToggleOn?: boolean,
    selectedForecastFolder?: string,
    selectedForecastFolderName?: string,
    selectedPdenSource?: number
  ) => void;
  isForecastToggleOn?: boolean;
  selectedForecastFolder?: string;
  selectedForecastFolderName?: string;
  selectedPdenSource?: number;
}

function OrgGeoCategory({
  toolbarActive = false,
  toolbarFocusedIndex = null,
  onSelectRef,
  onFieldClick,
  onFieldsAvailable,
  onFieldItemRef,
  active = false,
  focusedIndex = null
}: OrgGeoCategoryProps = {}) {
  const shortcutsEnabled = useContext(PresetDropdownContext);

  const [selectedJobId, setSelectedJobId] = useState<string | undefined>(undefined);
  const { data: jobs } = useGetGeoCalculationsJobs();

  const filteredJobs = useFilteredGeoCalculationsJobs(jobs);

  const selectedCat = useSelectedCategory();

  // Find the selected job's name for filtering columns (subgroup uses name)
  const selectedJobName = filteredJobs?.find((job) => job.jobId === selectedJobId)?.name;
  const orgGeoColumns = useOrgGeoSelector(selectedJobName);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any | null>(null);

  const { convert, getDisplayUnit } = useLengthConversion();
  const displayUnit = getDisplayUnit();

  const groupedColumns = useMemo(() => groupFields(orgGeoColumns), [orgGeoColumns]);

  const groupedFields = useMemo(
    () =>
      Array.from(groupedColumns).reduce((acc, [key, list]) => {
        // Ungated (no isOrgGeoProperty check): orgGeoColumns comes from
        // useOrgGeoSelector, so every field here is already org-geo — see
        // isOrgGeoProperty's docstring.
        const fields = list
          .map((field) => ({
            ...field,
            title: stripSubgroupFromTitle(field.title, field.subgroup)
          }))
          .sort((a, b) => a.withinSubgroupOrder - b.withinSubgroupOrder);
        if (fields.length > 0) {
          acc.push({ key, fields });
        }
        return acc;
      }, [] as Array<{ key: string; fields: IGroupByListColumn[] }>),
    [groupedColumns]
  );

  // Report select ref to parent when it changes
  useEffect(() => {
    if (onSelectRef) {
      onSelectRef(0, selectRef.current);
    }
  }, [onSelectRef, selectRef.current]);

  // Report field availability to parent when it changes
  useEffect(() => {
    if (onFieldsAvailable) {
      const fieldsCount =
        groupedFields && groupedFields.length > 0
          ? groupedFields.reduce((count, group) => count + group.fields.length, 0)
          : 0;
      onFieldsAvailable(fieldsCount);
    }
  }, [onFieldsAvailable, groupedFields]);

  const changeCalcJob = () => (value) => {
    setSelectedJobId(value);
    // Blur the select after selection
    if (selectRef.current) {
      selectRef.current.blur();
    }
  };

  // width based off of (--field-col-width - --space-4*2) / number of columns => (300-(16*2))/4 = 67
  const columns: ColumnsType<GeoCalculationsJob> = [
    {
      title: `Left Width (${displayUnit})`,
      dataIndex: "leftWidth",
      key: "leftWidth",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    },
    {
      title: `Right Width (${displayUnit})`,
      dataIndex: "rightWidth",
      key: "rightWidth",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    },
    {
      title: `Up Height (${displayUnit})`,
      dataIndex: "upHeight",
      key: "upHeight",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    },
    {
      title: `Down Height (${displayUnit})`,
      dataIndex: "downHeight",
      key: "downHeight",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    }
  ];

  if (!selectedCat?.isOrgGeoDefinedColumn || !filteredJobs) return null;
  const jobsOptions = filteredJobs?.length > 0 ? filteredJobs : [];
  const isFieldActive = (fieldStartIndex: number, fieldsLength: number) => {
    if (!focusedIndex) return null;

    if (
      focusedIndex >= fieldStartIndex &&
      focusedIndex < fieldStartIndex + fieldsLength
    ) {
      return focusedIndex - fieldStartIndex;
    }
    return null;
  };

  return (
    <OrgGeoCategoryWrapper>
      <WrapperRow>
        <div>
          <Wrapper>Name</Wrapper>
          <Wrapper>
            <Select
              ref={(el) => {
                selectRef.current = el;
              }}
              className={classnames("toolbar-item", {
                "keyboard-focused": toolbarActive && toolbarFocusedIndex === 0
              })}
              data-testid="geo-calc-job"
              value={selectedJobId}
              onChange={changeCalcJob()}
              options={jobsOptions.map((job) => ({ value: job.jobId, label: job.name }))}
              popupClassName="modal-select"
            />
          </Wrapper>
        </div>
        {selectedJobId && (
          <TableWrapperRow>
            <TableWrapper>
              <Table
                data-testid="geo-calc-table"
                dataSource={jobsOptions.filter((j) => j.jobId === selectedJobId)}
                columns={columns}
                rowKey="jobId"
                pagination={false}
                size="small"
                bordered={true}
              />
            </TableWrapper>
          </TableWrapperRow>
        )}
      </WrapperRow>
      {groupedFields && groupedFields.length > 0 && (
        <FieldListWrapper>
          {groupedFields.map((group, groupIndex) => {
            // Calculate the starting index for this group
            const fieldStartIndex = groupedFields
              .slice(0, groupIndex)
              .reduce((acc, prevGroup) => acc + prevGroup.fields.length, 0);

            return (
              <FieldGroup
                key={group.key}
                groupName={group.key}
                fields={group.fields}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                groups={groupedFields as any}
                dragHandleProps={null}
                isDragging={false}
                shortcutsEnabled={shortcutsEnabled}
                onFieldClick={onFieldClick}
                onItemRef={
                  onFieldItemRef
                    ? (relativeIndex: number, el: HTMLElement | null) => {
                        onFieldItemRef(fieldStartIndex + relativeIndex, el);
                      }
                    : undefined
                }
                active={active}
                focusedIndex={isFieldActive(fieldStartIndex, group.fields.length)}
              />
            );
          })}
        </FieldListWrapper>
      )}
    </OrgGeoCategoryWrapper>
  );
}

export default OrgGeoCategory;

const OrgGeoCategoryWrapper = styled.div`
  display: flex;
  max-height: 100%;
  flex-direction: column;
`;

const FieldListWrapper = styled.div`
  background-color: var(--color-white);
  padding-top: 5px;
  overflow: auto;
  flex-grow: 1;
`;

const Wrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr max-content;
  align-content: center;
  padding: 0 var(--space-2);
`;

const WrapperRow = styled.div`
  display: flex;
  padding: 0 var(--space-2) 5px var(--space-2);
  gap: 10px;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr max-content;
  align-content: center;
  padding: 0 var(--space-2);
`;

const TableWrapperRow = styled.div`
  display: flex;
  flex-direction: column;
`;
